import {
    selectAgentHasRead,
    selectHasAssignPermission,
    selectIsCalibration,
    selectIsDisputeEnabled,
    selectIsLoggedInUserAgent,
    selectIsScorableFinished,
} from "features/scorable/scorableSlice";
import { useAppSelector } from "app/hooks";
import { selectCanDeleteEvaluation } from "features/qualityForm/qualityFormSlice";
import { selectFormType } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import FormType from "enums/FormType";
import { SideDrawerMode } from "enums/SideDrawerMode";
import { HasEditScoreOrEditPermissionsContext } from "features/qualityForm/common/Contexts";
import React, { useContext } from "react";
import {
    AdminSaveScorableButton,
    AdminSubmitScorableButton,
    AgentCancelButton,
    AgentSaveButton,
    ApplyButton,
    ApplyCancelButton,
    OpenAssignViewButton,
    CancelRescoreModeButton,
    CreateCalibrationButton,
    DeleteEvaluationButton,
    RescoreButton,
    DisputeButton,
    DeclineDisputeButton,
} from "./ScorableButtons";
import { User } from "@genesys/cloud-frontend/dist/mjs/models";
import { SearchResult } from "features/qualityForm/formEditor/sidePanel/SearchDropdown";
import { ButtonGroup } from "components/ButtonGroup";
import { ReleaseDateSelectedType } from "features/qualityForm/formViewer/footer/releaseEvaluationTime/ReleaseEvaluationSelector";

export function AssignModeButtonSet({
    setSideDrawerMode,
    currentAssignee,
    onSelectedUser,
}: {
    setSideDrawerMode: React.Dispatch<React.SetStateAction<SideDrawerMode>>;
    currentAssignee: User;
    onSelectedUser: (input: SearchResult | undefined) => void;
}) {
    const buttonGroupStart = (
        <ApplyButton
            currentAssignee={currentAssignee}
            setSideDrawerMode={setSideDrawerMode}
        />
    );
    const buttonGroupEnd = (
        <ApplyCancelButton
            setSideDrawerMode={setSideDrawerMode}
            onSelectedUser={onSelectedUser}
        />
    );
    return (
        <ButtonGroup
            buttonGroupStart={buttonGroupStart}
            buttonGroupEnd={buttonGroupEnd}
        />
    );
}

export function RescoreModeButtonSet({
    calibrationMode,
    toggleCalibrationMode,
    calibrationError,
    toggleRescoreMode,
    setSideDrawerMode,
    attemptDispute,
    attemptDisputeDecline,
}: {
    calibrationMode: boolean;
    toggleCalibrationMode?: () => void;
    calibrationError: (response: string) => void;
    toggleRescoreMode: () => void;
    attemptDispute?: () => void;
    attemptDisputeDecline?: () => void;
    setSideDrawerMode: React.Dispatch<React.SetStateAction<SideDrawerMode>>;
}) {
    const isStatusFinished = useAppSelector(selectIsScorableFinished);

    // If the agent, they can only dispute using the agent view
    const isLoggedInUserAgent = useAppSelector(selectIsLoggedInUserAgent);

    const isDisputeEnabled = useAppSelector(selectIsDisputeEnabled);

    const showDisputeButton =
        !isLoggedInUserAgent && isDisputeEnabled && attemptDispute;

    const buttonGroupStart = (
        <>
            {calibrationMode && isStatusFinished ? (
                <CreateCalibrationButton
                    toggleCalibrationMode={toggleCalibrationMode}
                    calibrationError={calibrationError}
                />
            ) : (
                <RescoreButton toggleRescoreMode={toggleRescoreMode} />
            )}
            <OpenAssignViewButton setSideDrawerMode={setSideDrawerMode} />
            {showDisputeButton && (
                <DisputeButton attemptDispute={attemptDispute} />
            )}
        </>
    );

    const buttonGroupEnd = (
        <DeclineDisputeButton attemptDisputeDecline={attemptDisputeDecline} />
    );
    return (
        <ButtonGroup
            buttonGroupStart={buttonGroupStart}
            buttonGroupEnd={buttonGroupEnd}
        />
    );
}

export function AgentModeButtonSet({
    attemptSaveByAgent,
    attemptDisputeByAgent,
}: {
    attemptSaveByAgent?: () => void;
    attemptDisputeByAgent?: () => void;
}) {
    const isCalibration = !!useAppSelector(selectIsCalibration);

    const hasAgentReviewed = useAppSelector(selectAgentHasRead) ?? false;
    const isDisputeEnabled = useAppSelector(selectIsDisputeEnabled);

    const buttonGroupStart = (
        <>
            <AgentSaveButton attemptSaveByAgent={attemptSaveByAgent} />
            {isDisputeEnabled && (
                <DisputeButton attemptDispute={attemptDisputeByAgent} />
            )}
            <AgentCancelButton />
        </>
    );
    return (
        <ButtonGroup
            startCondition={!hasAgentReviewed && !isCalibration}
            buttonGroupStart={buttonGroupStart}
        />
    );
}

export function ScoringModeButtonSet({
    isAdmin,
    rescoreMode,
    toggleRescoreMode,
    attemptSubmit,
    attemptSubmitAndAssign,
    attemptSave,
    setSideDrawerMode,
    releaseDateData,
}: {
    isAdmin: boolean;
    rescoreMode: boolean;
    toggleRescoreMode: () => void;
    attemptSubmit: () => void;
    attemptSubmitAndAssign?: () => void;
    attemptSave: () => void;
    setSideDrawerMode: React.Dispatch<React.SetStateAction<SideDrawerMode>>;
    releaseDateData?: ReleaseDateSelectedType;
}) {
    const formType = useAppSelector(selectFormType);
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const hasEditScoreOrEditPermissions =
        useContext(HasEditScoreOrEditPermissionsContext)
            .hasEditScoreOrEditPermissions || formType === FormType.SURVEY;
    const canDeleteEvaluation = useAppSelector(selectCanDeleteEvaluation);
    const hasAssignPermission = useAppSelector(selectHasAssignPermission);
    const showAssignFunctionality =
        isNotCalibration && formType !== FormType.SURVEY && hasAssignPermission;
    const buttonGroupStart = (
        <>
            {hasEditScoreOrEditPermissions && (
                <AdminSubmitScorableButton
                    attemptSubmit={attemptSubmit}
                    attemptSubmitAndAssign={attemptSubmitAndAssign}
                    releaseDateData={releaseDateData}
                />
            )}
            {showAssignFunctionality && (
                <OpenAssignViewButton setSideDrawerMode={setSideDrawerMode} />
            )}
            {(hasEditScoreOrEditPermissions || showAssignFunctionality) && (
                <AdminSaveScorableButton
                    attemptSave={attemptSave}
                    rescoreMode={rescoreMode}
                    toggleRescoreMode={toggleRescoreMode}
                    isAdmin={isAdmin}
                />
            )}
            {hasEditScoreOrEditPermissions && (
                <CancelRescoreModeButton
                    rescoreMode={rescoreMode}
                    toggleRescoreMode={toggleRescoreMode}
                />
            )}
        </>
    );
    const buttonGroupEnd = <DeleteEvaluationButton />;

    return (
        <ButtonGroup
            endCondition={formType !== FormType.SURVEY && canDeleteEvaluation}
            buttonGroupStart={buttonGroupStart}
            buttonGroupEnd={buttonGroupEnd}
        />
    );
}
